import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { useAerodrome } from '../../api/plan'

const pad3 = (n: number) => String(Math.round(n) % 360 || 360).padStart(3, '0')

// An aerodrome's runways, radio frequencies, links and visual reporting
// points (#81), from the AIP.
export default function AerodromeDialog({ code, onClose }: { code: string | null; onClose: () => void }) {
  const { t } = useTranslation()
  const { data, isLoading, error } = useAerodrome(code)
  return (
    <Dialog open={code !== null} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {code}
        {data?.name ? ` · ${data.name}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? <CircularProgress size={24} /> : null}
        {error ? <Alert severity="error">{t('plan.aerodrome.loadFailed')}</Alert> : null}
        {data === null ? <Alert severity="info">{t('plan.aerodrome.none')}</Alert> : null}
        {data ? (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {[
                data.controlled ? t('plan.aerodrome.controlled') : t('plan.aerodrome.uncontrolled'),
                data.elevationFt !== null ? t('plan.aerodrome.elevation', { ft: data.elevationFt }) : null,
                data.variation !== null ? t('plan.aerodrome.variation', { deg: data.variation }) : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>

            {data.runways.length > 0 ? (
              <section>
                <Typography variant="subtitle2">{t('plan.aerodrome.runways')}</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('plan.aerodrome.runway')}</TableCell>
                      <TableCell>{t('plan.aerodrome.heading')}</TableCell>
                      <TableCell align="right">{t('plan.aerodrome.size')}</TableCell>
                      <TableCell>{t('plan.aerodrome.surface')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.runways.map((r) => (
                      <TableRow key={r.designator}>
                        <TableCell>{r.designator}</TableCell>
                        <TableCell>
                          {r.headingMag ? `${pad3(r.headingMag[0])}° / ${pad3(r.headingMag[1])}°` : '–'}
                        </TableCell>
                        <TableCell align="right">
                          {r.lengthM ?? '–'} × {r.widthM ?? '–'} m
                        </TableCell>
                        <TableCell>{r.surface ?? ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Typography variant="caption" color="text.secondary">
                  {t('plan.aerodrome.headingNote')}
                </Typography>
              </section>
            ) : null}

            {data.frequencies.length > 0 ? (
              <section>
                <Typography variant="subtitle2">{t('plan.aerodrome.frequencies')}</Typography>
                <Table size="small">
                  <TableBody>
                    {data.frequencies.map((f) => (
                      <TableRow key={`${f.kind}-${f.mhz}`}>
                        <TableCell>{t(`plan.aerodrome.kind.${f.kind}`)}</TableCell>
                        <TableCell>{f.mhz}</TableCell>
                        <TableCell>{f.callsign ?? ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </section>
            ) : null}

            {data.vrps.length > 0 ? (
              <section>
                <Typography variant="subtitle2">{t('plan.aerodrome.vrps')}</Typography>
                <Typography variant="body2">{data.vrps.map((v) => v.name ?? v.code).join(' · ')}</Typography>
              </section>
            ) : null}

            {data.links.length > 0 ? (
              <section>
                <Typography variant="subtitle2">{t('plan.aerodrome.links')}</Typography>
                <Stack>
                  {data.links.map((l) => (
                    <Link key={l.url} href={l.url} target="_blank" rel="noreferrer" variant="body2">
                      {l.label} <Icon icon="mdi:open-in-new" />
                    </Link>
                  ))}
                </Stack>
              </section>
            ) : null}

            <Typography variant="caption" color="text.secondary">
              {t('plan.aerodrome.fromAip', { revision: data.sourceRevision ?? '' })}
            </Typography>
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  )
}
